import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Section } from '@/components/ui/Section';
import {
  Upload,
  BarChart3,
  FileText,
  ArrowRight,
  FolderOpen,
} from 'lucide-react';

interface WelcomeStateProps {
  firstName?: string;
}

const steps = [
  {
    icon: <Upload className="h-5 w-5" />,
    title: 'Upload a schedule',
    description: 'Import a Primavera P6 XER file to create your first project.',
  },
  {
    icon: <BarChart3 className="h-5 w-5" />,
    title: 'Analyse automatically',
    description:
      'Health checks, critical-path analysis, and data-quality scoring run instantly.',
  },
  {
    icon: <FileText className="h-5 w-5" />,
    title: 'Report and track',
    description:
      'Generate reports, set baselines, and monitor changes across snapshots.',
  },
];

const WelcomeState: React.FC<WelcomeStateProps> = ({ firstName }) => {
  const navigate = useNavigate();
  const greeting = firstName ? `Welcome, ${firstName}` : 'Welcome';

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="flex flex-col items-center text-center py-8">
        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 mb-4">
          <FolderOpen className="h-6 w-6 text-primary" />
        </div>
        <h1 className="text-xl font-semibold text-foreground">{greeting}</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-md">
          Get started by creating your first project. Upload a P6 schedule and
          the platform does the rest.
        </p>
        <Button className="mt-6" size="sm" onClick={() => navigate('/app/upload')}>
          <Upload className="mr-2 h-4 w-4" />
          Create Your First Project
        </Button>
      </div>

      {/* How it works */}
      <Section title="How it works">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((step, idx) => (
            <div key={step.title} className="flex items-start gap-3">
              <div className="flex items-center justify-center shrink-0 h-8 w-8 rounded-md bg-muted text-muted-foreground">
                {step.icon}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">
                  <span className="text-muted-foreground mr-1">{idx + 1}.</span>
                  {step.title}
                </p>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Quick links */}
      <Section title="Quick links">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => navigate('/app/upload')}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-3 text-left hover:bg-muted/50 transition-colors group"
          >
            <Upload className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                New Project
              </p>
              <p className="text-[11px] text-muted-foreground">
                Upload a XER file to get started
              </p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>

          <button
            type="button"
            onClick={() => navigate('/app/projects')}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-3 text-left hover:bg-muted/50 transition-colors group"
          >
            <FolderOpen className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
                Browse Projects
              </p>
              <p className="text-[11px] text-muted-foreground">
                View all projects in your organisation
              </p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </div>
      </Section>
    </div>
  );
};

export default WelcomeState;
